import React, { useEffect, useState } from 'react';
import { KeyRound, RefreshCw, ShieldAlert, Users, Plus, Trash2, ScrollText, UserMinus } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' };
const td = { padding: '11px 12px', fontSize: 13, color: 'var(--ink)' };
const mono = { fontFamily: 'monospace', fontSize: 11 };

// Etiqueta legible + color por estado de firma Authenticode.
const SIG = {
  valid:          { label: 'Firmado',        color: 'var(--green)' },
  unsigned:       { label: 'Sin firma',      color: 'var(--red)' },
  untrusted_root: { label: 'Raíz no confiable', color: 'var(--amber)' },
  hash_mismatch:  { label: 'Hash alterado',  color: 'var(--red)' },
  expired:        { label: 'Cert vencido',   color: 'var(--amber)' },
  revoked:        { label: 'Cert revocado',  color: 'var(--red)' },
  distrust:       { label: 'Bloqueado',      color: 'var(--red)' },
  error:          { label: 'Error',          color: 'var(--ink-3)' },
};
const sigBadge = (s) => {
  const m = SIG[s] || { label: s || '—', color: 'var(--ink-3)' };
  return <span className="badge" style={{ color: m.color }}>{m.label}</span>;
};

const DECISION = {
  allow:     { label: 'Permitir',  color: 'var(--green)' },
  approval:  { label: 'Aprobación', color: 'var(--amber)' },
  deny:      { label: 'Denegar',   color: 'var(--red)' },
  no_policy: { label: 'Sin política', color: 'var(--ink-3)' },
};
const decisionBadge = (d) => {
  const m = DECISION[d] || { label: d, color: 'var(--ink-3)' };
  return <span className="badge" style={{ color: m.color }}>{m.label}</span>;
};

function Stat({ label, value, danger }) {
  return (
    <div style={{ flex: '1 1 130px', minWidth: 120, padding: '12px 14px', borderRight: '1px solid var(--border)' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: danger && value > 0 ? 'var(--red)' : 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{label}</div>
    </div>
  );
}

export default function Epm() {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [summary, setSummary] = useState(null);
  const [software, setSoftware] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [observations, setObservations] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [demote, setDemote] = useState({ assets: [], summary: {} });
  const [allowlist, setAllowlist] = useState([]);
  const [alPattern, setAlPattern] = useState('');
  const [scope, setScope] = useState([]);
  const [scPattern, setScPattern] = useState('');
  const [loading, setLoading] = useState(true);
  const [pform, setPform] = useState({ scope: 'global', scope_id: '', match_type: 'publisher', match_value: '', mode: 'deny', note: '' });
  const [psaving, setPsaving] = useState(false);
  const [perr, setPerr] = useState('');
  // Solapas + filtros
  const [tab, setTab] = useState('software');
  const [swStatus, setSwStatus] = useState('risky');   // filtro de estado de firma (server-side)
  const [swQuery, setSwQuery] = useState('');
  const [obsDecision, setObsDecision] = useState('all');
  const [obsSig, setObsSig] = useState('all');
  const [obsQuery, setObsQuery] = useState('');
  const [admQuery, setAdmQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [s, sw, la, ob, po, dr, al, sc] = await Promise.all([
        fetch(`${API_URL}/epm/summary`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/epm/software?status=${swStatus}&limit=500`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/epm/local-admins`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/epm/observations`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/epm/policies`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/epm/demote-report`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/epm/admin-allowlist`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/epm/demote-scope`, { headers }).then(r => r.json()),
      ]);
      setSummary(s || null);
      setSoftware(Array.isArray(sw.software) ? sw.software : []);
      setAdmins(Array.isArray(la.assets) ? la.assets : []);
      setObservations(Array.isArray(ob.observations) ? ob.observations : []);
      setPolicies(Array.isArray(po.policies) ? po.policies : []);
      setDemote({ assets: Array.isArray(dr.assets) ? dr.assets : [], summary: dr.summary || {} });
      setAllowlist(Array.isArray(al.allowlist) ? al.allowlist : []);
      setScope(Array.isArray(sc.scope) ? sc.scope : []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Al cambiar el filtro de estado de firma, se re-baja solo el software (server-side).
  const swFirst = React.useRef(true);
  useEffect(() => {
    if (swFirst.current) { swFirst.current = false; return; }
    fetch(`${API_URL}/epm/software?status=${swStatus}&limit=500`, { headers })
      .then(r => r.json()).then(d => setSoftware(Array.isArray(d.software) ? d.software : [])).catch(() => {});
    /* eslint-disable-next-line */
  }, [swStatus]);

  const addPolicy = async () => {
    if (pform.match_type !== 'any' && !pform.match_value.trim()) { setPerr('Falta el valor a matchear.'); return; }
    if (pform.scope !== 'global' && !pform.scope_id.trim()) { setPerr('Falta el alcance (equipo/usuario).'); return; }
    setPsaving(true); setPerr('');
    try {
      const body = { ...pform, scope_id: pform.scope === 'global' ? null : pform.scope_id.trim(), match_value: pform.match_type === 'any' ? null : pform.match_value.trim() };
      const r = await fetch(`${API_URL}/epm/policies`, { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setPform({ scope: 'global', scope_id: '', match_type: 'publisher', match_value: '', mode: 'deny', note: '' });
      load();
    } catch (e) { setPerr(e.message); }
    finally { setPsaving(false); }
  };
  const delPolicy = async (id) => {
    if (!window.confirm('¿Eliminar esta política?')) return;
    const r = await fetch(`${API_URL}/epm/policies/${id}`, { method: 'DELETE', headers });
    if (r.ok) load(); else alert('Error eliminando');
  };

  // F3: crea un comando epm_elevate (firmado por aprobador + hash-binding). Queda pendiente de
  // aprobación en el teléfono; el agente lo ejecuta recién con la firma.
  const elevate = async (o) => {
    if (!o.sha256) { alert('Sin hash del ejecutable — no se puede elevar de forma segura.'); return; }
    if (!window.confirm(`Solicitar elevación de "${o.name}" en ${o.hostname}?\n\nQueda pendiente de aprobación en el teléfono del aprobador; el equipo la ejecuta recién con la firma.`)) return;
    try {
      const r = await fetch(`${API_URL}/commands`, {
        method: 'POST', headers,
        body: JSON.stringify({ asset_id: o.asset_id, command_type: 'epm_elevate',
          payload: { exe_path: o.exe_path, sha256: o.sha256, publisher: o.publisher, user_name: o.user_name } }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      alert('Elevación solicitada. Aprobala desde el teléfono para que el equipo la ejecute.');
    } catch (e) { alert('No se pudo solicitar: ' + e.message); }
  };

  const addAllow = async () => {
    const pattern = alPattern.trim();
    if (!pattern) return;
    const r = await fetch(`${API_URL}/epm/admin-allowlist`, { method: 'POST', headers, body: JSON.stringify({ pattern }) });
    if (r.ok) { setAlPattern(''); load(); } else { const d = await r.json().catch(() => ({})); alert(d.error || 'Error agregando.'); }
  };
  const delAllow = async (id) => {
    const r = await fetch(`${API_URL}/epm/admin-allowlist/${id}`, { method: 'DELETE', headers });
    if (r.ok) load(); else alert('Error eliminando.');
  };
  // EPM F4: dispara el demote (quita admin) de un equipo. Cada quita va a firma en el teléfono.
  const runDemote = async (a) => {
    const accts = a.members.filter(m => m.action === 'remove').map(m => m.name);
    if (!accts.length) return;
    if (!window.confirm(`¿Quitar admin local en ${a.hostname} a: ${accts.join(', ')}?\n\nCada quita queda pendiente de aprobación firmada en el teléfono. La cuenta NO se borra (pasa a usuario estándar). Se preservan el break-glass y la allowlist.`)) return;
    try {
      const r = await fetch(`${API_URL}/epm/demote/${a.asset_id}`, { method: 'POST', headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      alert(`Encoladas ${d.count} quita(s) para ${a.hostname}. Aprobalas en el teléfono. Ticket #${d.ticket || '—'}.`);
      load();
    } catch (e) { alert('No se pudo: ' + e.message); }
  };
  const addScope = async () => {
    const pattern = scPattern.trim();
    if (!pattern) return;
    const r = await fetch(`${API_URL}/epm/demote-scope`, { method: 'POST', headers, body: JSON.stringify({ pattern }) });
    if (r.ok) { setScPattern(''); load(); } else { const d = await r.json().catch(() => ({})); alert(d.error || 'Error agregando.'); }
  };
  const delScope = async (id) => {
    const r = await fetch(`${API_URL}/epm/demote-scope/${id}`, { method: 'DELETE', headers });
    if (r.ok) load(); else alert('Error eliminando.');
  };

  const s = summary || {};
  const sinDatos = (s.con_firma_escaneada || 0) === 0;
  const dsum = demote.summary || {};

  // Filtros client-side
  const inc = (v, q) => (v || '').toLowerCase().includes(q.toLowerCase());
  const swQ = swQuery.trim();
  const softwareF = software.filter(x => !swQ || inc(x.hostname, swQ) || inc(x.name, swQ) || inc(x.signer, swQ) || inc(x.exe_path, swQ));
  const obsQ = obsQuery.trim();
  const observationsF = observations.filter(o =>
    (obsDecision === 'all' || o.decision === obsDecision) &&
    (obsSig === 'all' || o.sig_status === obsSig) &&
    (!obsQ || inc(o.hostname, obsQ) || inc(o.name, obsQ) || inc(o.exe_path, obsQ) || inc(o.user_name, obsQ) || inc(o.publisher, obsQ)));
  const admQ = admQuery.trim();
  const adminsF = admins.filter(a => !admQ || inc(a.hostname, admQ) || inc(a.user_name, admQ));

  const TABS = [
    { id: 'software', label: 'Software sin firma', n: software.length },
    { id: 'procesos', label: 'Procesos elevados', n: observations.length },
    { id: 'admins', label: 'Admins locales', n: admins.length },
    { id: 'demote', label: 'Demote', n: demote.assets.length },
    { id: 'policies', label: 'Políticas', n: policies.length },
  ];
  const inputSm = { padding: '5px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--ink)' };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ background: '#E6F1FB', borderRadius: 10, padding: 8 }}><KeyRound size={20} color="var(--accent)" /></div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>Privilegios (EPM)</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Visibilidad de software sin firma confiable y administradores locales</div>
          </div>
        </div>
        <button className="btn btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Stat strip */}
      <div className="card" style={{ padding: 0, display: 'flex', flexWrap: 'wrap', marginBottom: 16, overflow: 'hidden' }}>
        <Stat label="Software firmado" value={s.firmados || 0} />
        <Stat label="Sin firma" value={s.sin_firma || 0} danger />
        <Stat label="No confiables" value={s.no_confiables || 0} danger />
        <Stat label="Riesgosos (total)" value={s.riesgosos || 0} danger />
        <Stat label="Equipos con riesgo" value={s.equipos_con_riesgo || 0} danger />
        <Stat label="Equipos con admin local" value={s.equipos_con_admin_local || 0} />
      </div>

      {/* Solapas */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            background: 'none', border: 'none', borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === t.id ? 'var(--accent)' : 'var(--ink-2)', marginBottom: -1,
          }}>{t.label} <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({t.n})</span></button>
        ))}
      </div>

      {loading ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <>
          {/* Software sin firma confiable */}
          {tab === 'software' && (
          <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 7 }}>
                <ShieldAlert size={16} color="var(--amber)" /> Software sin firma confiable <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({softwareF.length})</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select style={inputSm} value={swStatus} onChange={e => setSwStatus(e.target.value)}>
                  <option value="risky">Riesgoso</option><option value="unsigned">Sin firma</option>
                  <option value="untrusted">No confiable</option><option value="all">Todos</option>
                </select>
                <input style={{ ...inputSm, width: 180 }} placeholder="Buscar equipo / software…" value={swQuery} onChange={e => setSwQuery(e.target.value)} />
              </div>
            </div>
            {sinDatos ? (
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 8 }}>
                Todavía no hay datos de firma. Requiere el <strong>agente 2.15.0+</strong> — a medida que los equipos reporten, aparecerá acá el software sin firma o con firma no confiable.
              </div>
            ) : softwareF.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8 }}>{software.length === 0 ? 'Sin software para este filtro. ✓' : 'Ningún resultado con los filtros actuales.'}</div>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>Equipo</th><th style={th}>Software</th><th style={th}>Firma</th><th style={th}>Firmante</th><th style={th}>Ejecutable</th>
                  </tr></thead>
                  <tbody>
                    {softwareF.map((x, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={{ ...td, fontWeight: 600 }}>{x.hostname}</td>
                        <td style={td}>{x.name}{x.version ? <span style={{ color: 'var(--ink-3)' }}> · {x.version}</span> : ''}</td>
                        <td style={td}>{sigBadge(x.sig_status)}</td>
                        <td style={{ ...td, color: 'var(--ink-2)' }}>{x.signer || '—'}</td>
                        <td style={{ ...td, ...mono, color: 'var(--ink-3)' }}>{x.exe_path || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Administradores locales */}
          {tab === 'admins' && (
          <div className="card" style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 7 }}>
                <Users size={16} color="var(--accent)" /> Equipos con administrador local <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({adminsF.length})</span>
              </div>
              <input style={{ ...inputSm, width: 180 }} placeholder="Buscar equipo / usuario…" value={admQuery} onChange={e => setAdmQuery(e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>Candidatos a reducción de privilegios (ver solapa Demote para quitar admin).</div>
            {adminsF.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{admins.length === 0 ? 'Sin equipos con administrador local reportados.' : 'Ningún resultado con la búsqueda actual.'}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>Equipo</th><th style={th}>Usuario</th><th style={th}>Admins locales</th><th style={th}>UAC</th><th style={th}>Score</th>
                  </tr></thead>
                  <tbody>
                    {adminsF.map((a, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={{ ...td, fontWeight: 600 }}>{a.hostname}</td>
                        <td style={{ ...td, color: 'var(--ink-2)' }}>{a.user_name || '—'}</td>
                        <td style={td}>{a.local_admins_count ?? (a.is_local_admin ? '≥1' : '—')}</td>
                        <td style={td}>{a.uac_enabled === false
                          ? <span className="badge" style={{ color: 'var(--red)' }}>UAC off</span>
                          : a.uac_enabled === true ? <span className="badge" style={{ color: 'var(--green)' }}>UAC on</span> : '—'}</td>
                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{a.security_score ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* F4 Paso A: reporte de demote de admins locales (solo lectura) + allowlist */}
          {tab === 'demote' && (
          <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 7 }}>
              <UserMinus size={16} color="var(--amber)" /> Demote de admins locales — reporte <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({demote.assets.length} equipos)</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>
              Qué cuentas se <b>quitarían</b> del grupo Administradores y cuáles se <b>preservan</b>. <b>Solo reporte</b>: todavía no toca ninguna máquina.
              Se preservan siempre la cuenta de respaldo (break-glass), los grupos de dominio y lo que agregues a la allowlist.
              {dsum.scope_activo && <span style={{ color: 'var(--amber)' }}> · Alcance activo: solo las sucursales listadas abajo.</span>}
            </div>

            {/* Alcance por sucursal (patrones de hostname a INCLUIR) */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Sucursales en alcance (patrón de hostname · soporta *) — vacío = todos los equipos</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {scope.map(sc => (
                  <span key={sc.id} className="badge" style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={mono}>{sc.pattern}</span>
                    <button onClick={() => delScope(sc.id)} title="Quitar del alcance" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 0, display: 'inline-flex' }}><Trash2 size={11} /></button>
                  </span>
                ))}
                <input className="form-input" value={scPattern} onChange={e => setScPattern(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addScope(); }} placeholder="Ej: CDM-ABA-*" style={{ width: 160 }} />
                <button className="btn btn-secondary btn-sm" onClick={addScope} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Plus size={12} /> Agregar</button>
              </div>
            </div>

            {/* Stat strip del reporte */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              <Stat label="Equipos" value={dsum.equipos || 0} />
              <Stat label="Con cuentas a quitar" value={dsum.equipos_con_quita || 0} />
              <Stat label="Cuentas a quitar" value={dsum.cuentas_a_quitar || 0} danger />
              <Stat label="Riesgo (quedaría 0 admin)" value={dsum.equipos_riesgo_cero_admin || 0} danger />
            </div>

            {/* Allowlist */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Cuentas a preservar (allowlist · soporta *)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {allowlist.map(a => (
                  <span key={a.id} className="badge" style={{ color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={mono}>{a.pattern}</span>
                    <button onClick={() => delAllow(a.id)} title="Quitar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 0, display: 'inline-flex' }}><Trash2 size={11} /></button>
                  </span>
                ))}
                <input className="form-input" value={alPattern} onChange={e => setAlPattern(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addAllow(); }} placeholder="Ej: svc-* o soporteIT" style={{ width: 180 }} />
                <button className="btn btn-secondary btn-sm" onClick={addAllow} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Plus size={12} /> Agregar</button>
              </div>
            </div>

            {demote.assets.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Sin equipos con admins locales reportados (requiere agente que reporte el grupo Administradores).</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>Equipo</th><th style={th}>Se quitaría</th><th style={th}>Se preserva</th><th style={{ ...th, textAlign: 'right' }}>Acción</th>
                  </tr></thead>
                  <tbody>
                    {demote.assets.map(a => (
                      <tr key={a.asset_id} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={{ ...td, fontWeight: 600 }}>
                          {a.hostname}
                          {a.would_leave_zero_admins && <span className="badge" style={{ color: 'var(--red)', marginLeft: 6 }} title="Quedaría sin ningún admin — no se tocará">⚠ 0 admin</span>}
                        </td>
                        <td style={td}>
                          {a.members.filter(m => m.action === 'remove').map((m, i) => (
                            <span key={i} className="badge" style={{ color: 'var(--red)', marginRight: 4 }} title={m.reason}>{m.name}</span>
                          ))}
                          {a.remove_count === 0 && <span style={{ color: 'var(--ink-3)' }}>—</span>}
                        </td>
                        <td style={td}>
                          {a.members.filter(m => m.action === 'preserve').map((m, i) => (
                            <span key={i} className="badge" style={{ color: 'var(--green)', marginRight: 4 }} title={m.reason}>{m.name}</span>
                          ))}
                          {a.preserve_count === 0 && <span style={{ color: 'var(--ink-3)' }}>—</span>}
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-danger btn-sm" disabled={a.remove_count === 0 || a.would_leave_zero_admins}
                            title={a.would_leave_zero_admins ? 'Quedaría sin admin — completá el break-glass primero' : 'Quitar admin (requiere firma en el teléfono)'}
                            onClick={() => runDemote(a)}>Quitar admin</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Observaciones: procesos que corrieron elevados + qué decidiría la política */}
          {tab === 'procesos' && (
          <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 7 }}>
                <ScrollText size={16} color="var(--accent)" /> Procesos elevados observados <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({observationsF.length})</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select style={inputSm} value={obsDecision} onChange={e => setObsDecision(e.target.value)}>
                  <option value="all">Toda decisión</option><option value="deny">Denegar</option><option value="approval">Aprobación</option><option value="allow">Permitir</option><option value="no_policy">Sin política</option>
                </select>
                <select style={inputSm} value={obsSig} onChange={e => setObsSig(e.target.value)}>
                  <option value="all">Toda firma</option><option value="valid">Firmado</option><option value="unsigned">Sin firma</option><option value="untrusted_root">Raíz no confiable</option><option value="hash_mismatch">Hash alterado</option>
                </select>
                <input style={{ ...inputSm, width: 170 }} placeholder="Buscar proceso / equipo…" value={obsQuery} onChange={e => setObsQuery(e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>Modo auditoría: el agente registra qué corrió elevado; la columna "Decisión" muestra qué haría la política vigente (todavía no se bloquea nada).</div>
            {observationsF.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{observations.length === 0 ? 'Sin observaciones todavía — requiere agente 2.16.0+.' : 'Ningún resultado con los filtros actuales.'}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>Equipo</th><th style={th}>Proceso</th><th style={th}>Firma</th><th style={th}>Publisher</th><th style={th}>Usuario</th><th style={th}>Decisión</th><th style={{ ...th, textAlign: 'right' }}>Acción</th>
                  </tr></thead>
                  <tbody>
                    {observationsF.map((o, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={{ ...td, fontWeight: 600 }}>{o.hostname}</td>
                        <td style={td}>{o.name}<div style={{ ...mono, color: 'var(--ink-3)' }}>{(o.exe_path || '').slice(0, 46)}</div></td>
                        <td style={td}>{sigBadge(o.sig_status)}</td>
                        <td style={{ ...td, color: 'var(--ink-2)' }}>{o.publisher || '—'}</td>
                        <td style={{ ...td, color: 'var(--ink-2)' }}>{o.user_name || '—'}</td>
                        <td style={td}>{decisionBadge(o.decision)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {o.decision !== 'deny' && <button className="btn btn-secondary btn-sm" onClick={() => elevate(o)} title="Solicitar elevación (requiere aprobación en el teléfono)">Elevar</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Políticas de elevación */}
          {tab === 'policies' && (
          <div className="card" style={{ padding: '18px 20px' }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--ink)' }}>Políticas de elevación <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({policies.length})</span></div>

            {/* Alta rápida */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--ink-2)', display: 'block' }}>Alcance</label>
                <select className="form-input" value={pform.scope} onChange={e => setPform(f => ({ ...f, scope: e.target.value }))}>
                  <option value="global">Global</option><option value="asset">Equipo</option><option value="user">Usuario</option>
                </select>
              </div>
              {pform.scope !== 'global' && (
                <div><label style={{ fontSize: 11, color: 'var(--ink-2)', display: 'block' }}>{pform.scope === 'asset' ? 'ID de equipo' : 'Usuario'}</label>
                  <input className="form-input" value={pform.scope_id} onChange={e => setPform(f => ({ ...f, scope_id: e.target.value }))} /></div>
              )}
              <div>
                <label style={{ fontSize: 11, color: 'var(--ink-2)', display: 'block' }}>Match</label>
                <select className="form-input" value={pform.match_type} onChange={e => setPform(f => ({ ...f, match_type: e.target.value }))}>
                  <option value="publisher">Publisher</option><option value="sha256">SHA-256</option><option value="path">Ruta</option><option value="any">Cualquiera</option>
                </select>
              </div>
              {pform.match_type !== 'any' && (
                <div style={{ flex: '1 1 200px' }}><label style={{ fontSize: 11, color: 'var(--ink-2)', display: 'block' }}>Valor</label>
                  <input className="form-input" style={{ width: '100%' }} value={pform.match_value} onChange={e => setPform(f => ({ ...f, match_value: e.target.value }))} placeholder={pform.match_type === 'publisher' ? 'Ej: WireGuard LLC' : pform.match_type === 'path' ? 'Ej: C:\\Program Files\\App\\' : 'hash…'} /></div>
              )}
              <div>
                <label style={{ fontSize: 11, color: 'var(--ink-2)', display: 'block' }}>Modo</label>
                <select className="form-input" value={pform.mode} onChange={e => setPform(f => ({ ...f, mode: e.target.value }))}>
                  <option value="allow">Permitir</option><option value="approval">Aprobación</option><option value="deny">Denegar</option>
                </select>
              </div>
              <button className="btn btn-primary btn-sm" onClick={addPolicy} disabled={psaving} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Plus size={13} /> {psaving ? '…' : 'Agregar'}
              </button>
            </div>
            {perr && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{perr}</div>}

            {policies.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Sin políticas. Con reglas cargadas, la columna "Decisión" de arriba deja de ser "Sin política".</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>Alcance</th><th style={th}>Match</th><th style={th}>Valor</th><th style={th}>Modo</th><th style={th}></th>
                  </tr></thead>
                  <tbody>
                    {policies.map(p => (
                      <tr key={p.id} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={td}>{p.scope}{p.scope_id ? <span style={{ color: 'var(--ink-3)' }}> · {p.scope_id}</span> : ''}</td>
                        <td style={td}>{p.match_type}</td>
                        <td style={{ ...td, ...mono, color: 'var(--ink-2)' }}>{p.match_value || '(cualquiera)'}</td>
                        <td style={td}>{decisionBadge(p.mode)}</td>
                        <td style={{ ...td, textAlign: 'right' }}><button className="btn btn-danger btn-sm" onClick={() => delPolicy(p.id)}><Trash2 size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}
        </>
      )}
    </div>
  );
}
